import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { StatCard, Skeleton } from "@/components/domain/Bits";
import { money, num, timeAgo } from "@/lib/format";
import {
  DollarSign, ShoppingCart, Ticket, Clock, TrendingUp, Users, AlertTriangle, Activity, ArrowRight,
} from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";

const CHART = [
  { d: "Lun", v: 42000 }, { d: "Mar", v: 68000 }, { d: "Mié", v: 51000 }, { d: "Jue", v: 89000 },
  { d: "Vie", v: 120000 }, { d: "Sáb", v: 210000 }, { d: "Dom", v: 284000 },
];

const ACTION_LABEL = {
  "payment.approve": "aprobó un pago", "payment.reject": "rechazó un comprobante",
  "payment.correction": "solicitó corrección", "raffle.create": "creó un sorteo",
  "raffle.update": "actualizó un sorteo", "reservation.created": "generó una reserva",
  "payment.proof_uploaded": "recibió un comprobante", "winner.published": "publicó un ganador",
  "raffle.result_generated": "generó un resultado",
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  return (
    <AdminLayout title="Dashboard">
      {!d ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0,1,2,3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Ventas hoy" value={money(284000)} delta="↑ 18% vs ayer" icon={DollarSign} testid="kpi-revenue" />
            <StatCard label="Ingresos confirmados" value={money(d.revenue)} icon={TrendingUp} testid="kpi-total-revenue" />
            <StatCard label="Boletos vendidos" value={num(d.tickets_sold)} icon={Ticket} testid="kpi-tickets" />
            <StatCard label="Conversión" value={`${d.conversion}%`} icon={ShoppingCart} testid="kpi-conversion" />
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between"><h3 className="font-semibold text-slate-900">Ventas de la semana</h3><span className="font-mono text-sm font-semibold text-emerald-600">↑ 24%</span></div>
              <div className="mt-6 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={CHART}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="d" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} />
                    <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="v" fill="#1D4ED8" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-semibold text-slate-900"><AlertTriangle className="h-4 w-4 text-amber-500" />Centro de alertas</h3>
              <div className="mt-4 space-y-2">
                <Alert label="Comprobantes sin revisar" value={d.alerts.proofs_to_review} to="/admin/validacion?f=in_review" tone="warning" />
                <Alert label="Reservas próximas a vencer" value={d.alerts.expiring_reservations} to="/admin/validacion" tone="warning" />
                <Alert label="Pagos pendientes" value={d.alerts.pending_payments} to="/admin/validacion" tone="default" />
                <Alert label="Sorteos activos" value={d.active_raffles} to="/admin/sorteos" tone="success" />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Activity className="h-4 w-4 text-brand" />Actividad reciente</h3>
            <div className="mt-4 divide-y divide-slate-100">
              {d.activity.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-3" data-testid="activity-item">
                  <div className="flex items-center gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{(a.user || "S")[0].toUpperCase()}</div>
                    <div className="text-sm"><span className="font-semibold text-slate-900">{(a.user || "sistema").split("@")[0]}</span> <span className="text-slate-500">{ACTION_LABEL[a.action] || a.action}</span> <span className="font-mono text-xs text-slate-400">{a.entity_id}</span></div>
                  </div>
                  <span className="text-xs text-slate-400">{timeAgo(a.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const Alert = ({ label, value, to, tone }) => {
  const tones = { warning: "text-amber-600 bg-amber-50", danger: "text-red-600 bg-red-50", success: "text-emerald-600 bg-emerald-50", default: "text-slate-600 bg-slate-100" };
  return (
    <Link to={to} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 hover:bg-slate-50" data-testid={`alert-${label}`}>
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 font-mono text-sm font-bold ${tones[tone]}`}>{value} <ArrowRight className="h-3 w-3" /></span>
    </Link>
  );
};
